/*
 * Shared loading / empty / error presentation for CMMS.
 *
 * Before this, every list rolled its own three states: a centred CircularProgress, a
 * one-line italic "nothing here", and a red Typography with no way to recover. The
 * spinner was the worst of the three — it collapses the panel to a small centred dot,
 * so the layout jumps when data arrives and a slow tab looks broken rather than busy.
 *
 * Kept in one file because the three are always reasoned about together: a list that
 * gains a skeleton almost always wants the matching empty and error treatment too.
 */
import React from "react";
import { Box, Button, Skeleton, Stack, Typography } from "@mui/material";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import RefreshIcon from "@mui/icons-material/Refresh";
import { FormattedMessage, useIntl } from "react-intl";
import cmmsMessages from "../cmmsMessages";

// The code the API puts on every 500. Its message is a fixed English sentence, kept vague
// on purpose so nothing from the exception reaches the client.
const CMMS_INTERNAL_ERROR = "CMMS_INTERNAL_ERROR";

/**
 * Turns whatever a fetcher rejected with into something displayable.
 *
 * Replaces the local formatError copy that every list carried. Errors reach these
 * components as Error, as a bare string, or (from a failed parse) as an object with no
 * message at all, so all three shapes have to be handled.
 *
 * A 500 shows the caller's own fallback rather than the server's sentence: the fallback
 * is translated and says which operation failed, the server's says neither.
 */
export const formatCmmsError = (error, fallback) => {
  if (!error) return fallback;
  if (typeof error === "string") return error;
  if (error.code === CMMS_INTERNAL_ERROR) return fallback;
  return error.message ?? fallback;
};

/**
 * Placeholder rows shaped like the list that is loading.
 *
 * `rows` should roughly match what the list usually shows: too few and the panel still
 * jumps when data lands, too many and short lists flash a wall of grey.
 */
export function CmmsListSkeleton({ rows = 4, height = 32, sx }) {
  return (
    <Stack spacing={1} sx={{ py: 1, ...sx }} aria-busy="true">
      {Array.from({ length: rows }, (_, index) => (
        <Skeleton
          key={index}
          variant="rounded"
          height={height}
          // Ragged right edge reads as content rather than as a loading bar.
          width={index % 3 === 2 ? "82%" : "100%"}
          animation="wave"
        />
      ))}
    </Stack>
  );
}

/**
 * Empty state. `hint` is what makes it useful — "No work orders" alone leaves the user
 * guessing whether the list is broken, filtered, or genuinely empty.
 */
export function CmmsEmptyState({ icon, title, hint, action, dense = false }) {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        textAlign: "center",
        gap: 1,
        px: 2,
        py: dense ? 3 : 6,
      }}
    >
      {icon &&
        <Box sx={{ color: "text.disabled", display: "flex", "& > *": { fontSize: dense ? 28 : 40 } }}>
          {icon}
        </Box>
      }
      <Typography variant={dense ? "body2" : "subtitle1"} sx={{ fontWeight: 700 }}>
        {title}
      </Typography>
      {hint &&
        <Typography variant="body2" sx={{ color: "text.secondary", maxWidth: 420 }}>
          {hint}
        </Typography>
      }
      {action && <Box sx={{ mt: 1 }}>{action}</Box>}
    </Box>
  );
}

/**
 * Error state with a retry.
 *
 * `onRetry` is optional but should be passed wherever a react-query `refetch` exists —
 * without it a transient network blip leaves the panel dead until a full page reload.
 */
export function CmmsErrorState({ error, fallback, onRetry, isRetrying = false, dense = false }) {
  const intl = useIntl();
  const message = formatCmmsError(error, fallback ?? intl.formatMessage(cmmsMessages.errorGeneric));

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        textAlign: "center",
        gap: 1,
        px: 2,
        py: dense ? 2 : 4,
      }}
      role="alert"
    >
      <ErrorOutlineIcon sx={{ color: "error.main", fontSize: dense ? 24 : 32 }} />
      <Typography variant="body2" sx={{ color: "error.main", maxWidth: 460 }}>
        {message}
      </Typography>
      {onRetry &&
        <Button
          size="small"
          variant="outlined"
          color="secondary"
          startIcon={<RefreshIcon />}
          onClick={onRetry}
          disabled={isRetrying}
          sx={{ mt: 0.5 }}
        >
          <FormattedMessage {...cmmsMessages.retry} />
        </Button>
      }
    </Box>
  );
}
